// §26.3 — redeeming `https://hangar.ab8.org/#/connect/<token>`.
//
// §26.3: "Opening it while signed in creates the pair; while signed out it routes through sign-in
// first and resumes."
//
// **A hash, not a path**, and that is §26.3's choice rather than this file's — but it is the right
// one and worth saying why, because a `/connect/<token>` path looks tidier. The token is the secret
// (§26.9). A path is sent to the server on every navigation, lands in access logs, and rides the
// `Referer` of anything the page loads next; a fragment is never transmitted at all. §21's SPA
// fallback would also have to learn a second route, and §16's table would grow a public one.
//
// The token is removed from the address bar the moment it is read, before the network call. A link
// that stays in the URL is a link that gets copied out of it, re-shared, and bookmarked — and it is
// single-use, so every later use of that bookmark is a 410 the person cannot explain.

import { isInviteToken } from '@hangar/shared';

/** Where a token waits while somebody signs in. Session-scoped: a new tab is a new intention. */
export const PENDING_INVITE_KEY = 'hangar.pendingInvite';

const PATTERN = /^#\/connect\/([0-9a-f]{64})$/;

/**
 * The token in a hash, or `null`.
 *
 * Validated here rather than at the call site so that a malformed fragment is simply not an
 * invitation — the accept route would refuse it anyway, and asking it to is a network round trip
 * that tells an attacker their guess was well-formed.
 */
export function tokenInHash(hash: string): string | null {
  const match = PATTERN.exec(hash);
  const token = match?.[1];
  return token !== undefined && isInviteToken(token) ? token : null;
}

export interface ConnectLinkDeps {
  /** `window.location.hash`, and a way to clear it without adding a history entry. */
  readHash: () => string;
  clearHash: () => void;
  /** Session storage, injected so this is testable with a plain `Map`. */
  store: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;
  signedIn: () => boolean;
  accept: (token: string) => Promise<{ peer: string; displayName: string; already: boolean }>;
  /** §26.8 F14 and friends — reported to the user, never thrown into the boot path. */
  notify: (message: string) => void;
  /** Copy, so this module holds no strings of its own. */
  copy: { accepted: (name: string) => string; failed: string; pending: string };
}

export interface ConnectLinkResult {
  readonly outcome: 'none' | 'accepted' | 'already' | 'failed' | 'pending-sign-in';
  readonly peer?: string;
}

/**
 * Read the hash (or a token parked before a sign-in), redeem it, and say what happened.
 *
 * Called once per boot, **after** identity is known — the signed-out branch depends on it, and
 * running before would send a signed-in person through the sign-in they do not need.
 */
export async function redeemConnectLink(deps: ConnectLinkDeps): Promise<ConnectLinkResult> {
  const fromHash = tokenInHash(deps.readHash());
  // The hash is cleared *before* anything else can fail, so a token never survives its own attempt.
  if (fromHash !== null) deps.clearHash();

  const parked = deps.store.getItem(PENDING_INVITE_KEY);
  const token = fromHash ?? (parked !== null && isInviteToken(parked) ? parked : null);
  if (token === null) {
    // A parked value that is not a token is somebody else's key or a corrupted one; drop it rather
    // than carrying it into every future boot.
    if (parked !== null) deps.store.removeItem(PENDING_INVITE_KEY);
    return { outcome: 'none' };
  }

  if (!deps.signedIn()) {
    // §26.3 — "while signed out it routes through sign-in first and resumes". Parked rather than
    // redeemed, and the *user* is told why: a silent redirect to a sign-in page they did not ask
    // for is indistinguishable from a broken link.
    deps.store.setItem(PENDING_INVITE_KEY, token);
    deps.notify(deps.copy.pending);
    return { outcome: 'pending-sign-in' };
  }

  // Spent or refused, the parked copy goes: a token is single use, so retrying it on every boot
  // would turn one expired link into a permanent error message.
  deps.store.removeItem(PENDING_INVITE_KEY);
  try {
    const result = await deps.accept(token);
    deps.notify(deps.copy.accepted(result.displayName));
    return { outcome: result.already ? 'already' : 'accepted', peer: result.peer };
  } catch {
    // Every failure reads the same to the person holding the link — expired, spent, blocked, or the
    // server being down. The distinctions matter to §26.8 F14 and to nobody standing here.
    deps.notify(deps.copy.failed);
    return { outcome: 'failed' };
  }
}
